import React, { useMemo } from 'react';
import { WorldSection } from './WorldPageHeader';
import { CopyTextButton } from './CopyTextButton';
import { EntityImage } from './EntityImage';
import { MissingMonsterPhotos } from './MissingMonsterPhotos';
import { resolveImageEntries, useFailedImages } from '../../utils/entityImages';
import { unassignedImages } from '../../data/guide/entityImages';
import type { WorldData } from '../../types/world';

/** Panel del staff: estado de las imágenes, separado de drops, estadísticas, recetas y ubicaciones. */
export function ImageCheck({
  w
}: {
  w: WorldData;
}) {
  const {
    resolved
  } = useMemo(() => resolveImageEntries(w), [w]);
  const failed = useFailedImages();
  const rows = resolved.map(e => {
    const src = e.image?.src;
    const status = !src ? 'Sin imagen' : failed.includes(src) ? 'No carga' : e.image?.needsConfirmation ? 'Integrada · confirmar asociación' : 'Integrada';
    const id = e.kind === 'clase' ? e.classId?.toUpperCase() : e.resolvedMonsterIds.length ? e.resolvedMonsterIds.join(', ') : '—';
    const idNote = e.notInCatalog ? 'no está en 06_monsters: no se muestra' : e.ambiguous ? 'nombre con varios IDs: no se asigna' : '';
    return {
      e,
      status,
      id,
      idNote
    };
  });
  const pendingText = [...rows.filter(r => r.status !== 'Integrada').map(r => `${r.e.label} (${r.e.kind}, ID ${r.id}${r.idNote ? `, ${r.idNote}` : ''}) · ${r.status}${r.e.pending ? ` · ${r.e.pending}` : ''}`), ...unassignedImages.map(u => `Imagen sin asignar · ${u.description} · ${u.question} · ${u.src}`)].join('\n');
  return <WorldSection id="imagenes" title="Imágenes de clases, NPC y monstruos" count={rows.length} data-id="element-1283" data-mp="1jqbk3q137eln">
      <p className="mb-3 max-w-3xl text-sm text-gray-400" data-id="element-1284" data-mp="1ol7doulz2a0v">
        Imagen proporcionada por el staff: identifica a la entidad, no confirma drops, coordenadas, estadísticas ni recetas. Si una imagen no carga, aparece aquí al intentar mostrarla en este navegador.
      </p>
      <MissingMonsterPhotos w={w} data-id="element-1285" data-mp="3uh1oqg1dmwp8" />
      <div className="mb-3" data-id="element-1286" data-mp="3ui3bvivo39nb">
        <CopyTextButton text={pendingText || 'Sin imágenes pendientes.'} label="Copiar imágenes pendientes" data-id="element-1287" data-mp="11l9xjjv33m4l" />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10" data-id="element-1288" data-mp="wnqi4gmnh3b2">
        <table className="w-full min-w-[720px] text-left text-sm" data-id="element-1289" data-mp="1amzn0k5eoazq">
          <thead className="bg-white/[0.03] text-xs text-gray-400" data-id="element-1290" data-mp="1ak52ytqb0s6u">
            <tr data-id="element-1291" data-mp="16jbf20cv3ddy">
              <th className="px-4 py-3 font-semibold" data-id="element-1292" data-mp="16mz9r2u1m5u9">Imagen</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1293" data-mp="1kjm89a1n942a">Entidad</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1294" data-mp="1flue09dj15ux">ID</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1295" data-mp="1flayt0f9gvkl">Estado</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1296" data-mp="1u9fgq26hp32b">Procedencia y asociación</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5" data-id="element-1297" data-mp="1pcp5bs5mqvn8">
            {rows.map(({
            e,
            status,
            id,
            idNote
          }) => <tr key={e.key} data-id="element-1298" data-mp="23bf0h28w2l8y">
                <td className="px-4 py-3" data-id="element-1299" data-mp="1yc2fyo5hpq9j"><EntityImage entry={e} alt={e.label} kind={e.kind === 'monstruo' ? 'monstruo' : 'persona'} data-id="element-1300" data-mp="1yctxww65zphc" /></td>
                <td className="px-4 py-3 text-white" data-id="element-1301" data-mp="2hyvuscowys4k">{e.label}<span className="block text-xs text-gray-500" data-id="element-1302" data-mp="2hw1acl5wh7b5">{e.kind}</span></td>
                <td className="px-4 py-3 font-mono text-gray-200" data-id="element-1303" data-mp="2mw5qub6t621d">{id}{idNote && <span className="block font-sans text-xs text-amber-100" data-id="element-1304" data-mp="28w4f20p09a74">{idNote}</span>}</td>
                <td className="px-4 py-3 text-gray-200" data-id="element-1305" data-mp="2cxzq3wwqn1y3">{status}{e.pending && <span className="block text-xs text-gray-500" data-id="element-1306" data-mp="2d0lz80za1pif">{e.pending}</span>}</td>
                <td className="px-4 py-3 text-xs text-gray-400" data-id="element-1307" data-mp="1z59tkyjl2emq">{e.image ? <>Imagen proporcionada por el staff · {e.image.addedOn}<span className="block" data-id="element-1308" data-mp="23zw28tya5t34">{e.image.matchedBy}</span></> : '—'}</td>
              </tr>)}
            {unassignedImages.map(u => <tr key={u.src} data-id="element-1309" data-mp="1u91jhzk8tbmf">
                <td className="px-4 py-3" data-id="element-1310" data-mp="1u5x6ccosv5jm"><img src={u.src} alt={u.description} loading="lazy" className="h-10 w-10 rounded-lg border border-white/10 object-cover object-[50%_30%]" data-id="element-1311" data-mp="1g8qsmwixnx19" /></td>
                <td className="px-4 py-3 text-white" data-id="element-1312" data-mp="1l6sbphgczwcq">Sin asignar<span className="block text-xs text-gray-500" data-id="element-1313" data-mp="1d7f6gxuul3zt">{u.description}</span></td>
                <td className="px-4 py-3 font-mono text-gray-200" data-id="element-1314" data-mp="1882nwpb0ej31">—</td>
                <td className="px-4 py-3 text-amber-100" data-id="element-1315" data-mp="189vrfvqbsn6t">{u.question}</td>
                <td className="px-4 py-3 text-xs text-gray-400" data-id="element-1316" data-mp="13fj9xr8etlxp">Imagen proporcionada por el staff · no se muestra a los jugadores</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </WorldSection>;
}